// Firestore Database Operations
// User data and portfolio management

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore"
import { db } from "./config"
import { User } from "@/lib/user-context"
import { normalizeEmail } from "@/lib/utils"

const USERS_COLLECTION = "users"

/** Recursively remove undefined values from an object/array. Firestore rejects undefined. Leaves null intact. Applied to all user doc updates so both new and existing users are supported. */
function stripUndefined<T>(value: T): T {
  if (value === undefined) return value
  if (value === null || typeof value !== "object") return value
  if (Array.isArray(value)) return value.map((item) => stripUndefined(item)) as T
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as object)) {
    if (v !== undefined) out[k] = stripUndefined(v)
  }
  return out as T
}

// Convert Firestore timestamp to ISO string
function convertTimestamp(timestamp: any): string {
  if (timestamp?.toDate) {
    return timestamp.toDate().toISOString()
  }
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString()
  }
  return timestamp || new Date().toISOString()
}

// Convert User to Firestore document
function userToFirestore(user: User): any {
  return {
    ...user,
    createdAt: Timestamp.fromDate(new Date(user.createdAt)),
    portfolio: {
      ...user.portfolio,
      lastUpdated: Timestamp.fromDate(new Date(user.portfolio.lastUpdated)),
    },
  }
}

// Convert Firestore document to User
function firestoreToUser(docData: any, id: string): User {
  const email = docData.email || ""
  return {
    id,
    email,
    normalizedEmail: docData.normalizedEmail ?? (email ? normalizeEmail(email) : undefined),
    username: docData.username || "Unknown",
    avatar: docData.avatar,
    preferences: docData.preferences || {
      theme: "system",
      riskTolerance: "medium",
      investmentFocus: "diversified",
      notifications: {
        email: false,
        targetPrice: false,
        ratingChange: false,
        majorNews: false,
      },
    },
    portfolio: {
      holdings: docData.portfolio?.holdings || [],
      cash: docData.portfolio?.cash || 0,
      lastUpdated: convertTimestamp(docData.portfolio?.lastUpdated) || new Date().toISOString(),
    },
    transactions: docData.transactions?.map((tx: any) => {
      const dateStr = convertTimestamp(tx.transactionDate) || new Date().toISOString()
      return {
        ...tx,
        transactionDate: dateStr,
        executedAt: tx.executedAt ? convertTimestamp(tx.executedAt) : dateStr,
      }
    }) || [],
    createdAt: convertTimestamp(docData.createdAt) || new Date().toISOString(),
    isAdmin: docData.isAdmin || false,
  }
}

// Create user in Firestore (sets normalizedEmail and stores email normalized for lookups). Strips undefined so Firestore accepts the doc.
export async function createUserInFirestore(user: User): Promise<void> {
  const normalized = user.normalizedEmail ?? normalizeEmail(user.email)
  const userRef = doc(db, USERS_COLLECTION, user.id)
  const data = userToFirestore({ ...user, email: normalized, normalizedEmail: normalized })
  await setDoc(userRef, stripUndefined(data) as Record<string, unknown>)
}

// Get user from Firestore by ID
export async function getUserFromFirestore(userId: string): Promise<User | null> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) {
      console.log("[Firestore] getUserFromFirestore: no document for userId=", userId)
      return null
    }

    return firestoreToUser(userSnap.data(), userSnap.id)
  } catch (e) {
    console.error("[Firestore] getUserFromFirestore error:", e)
    console.error("[Firestore] userId:", userId, "code:", (e as any)?.code, "message:", (e as any)?.message)
    throw e
  }
}

// Get user from Firestore by email (pass normalized email). Tries normalizedEmail first, then email for legacy docs.
export async function getUserByEmailFromFirestore(normalizedEmail: string): Promise<User | null> {
  const usersRef = collection(db, USERS_COLLECTION)
  let q = query(usersRef, where("normalizedEmail", "==", normalizedEmail))
  let querySnapshot = await getDocs(q)
  if (querySnapshot.empty) {
    q = query(usersRef, where("email", "==", normalizedEmail))
    querySnapshot = await getDocs(q)
  }
  if (querySnapshot.empty) return null
  const doc = querySnapshot.docs[0]
  return firestoreToUser(doc.data(), doc.id)
}

// Update user in Firestore
export async function updateUserInFirestore(userId: string, updates: Partial<User>): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  const updateData: any = { ...updates }
  
  // Handle timestamp conversions
  if (updates.createdAt) {
    updateData.createdAt = Timestamp.fromDate(new Date(updates.createdAt))
  }
  if (updates.portfolio?.lastUpdated) {
    updateData.portfolio = {
      ...updates.portfolio,
      lastUpdated: Timestamp.fromDate(new Date(updates.portfolio.lastUpdated)),
    }
  }
  await updateDoc(userRef, stripUndefined(updateData) as Record<string, unknown>)
}

// Update user portfolio
// Note: This function uses the client SDK, so it requires the user to be authenticated
// and Firestore security rules must allow the update
export async function updateUserPortfolioInFirestore(
  userId: string,
  holdings: Array<{
    symbol: string
    name?: string
    shares: number
    avgCost: number
    purchaseDate: string
    notes?: string
    type?: "stock" | "etf" | "crypto"
    providerId?: string
  }>
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  
  // Check if user document exists
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}. Please ensure user is created first.`)
  }
  
  // Get existing user data to preserve cash and other fields
  const existingData = userSnap.data()
  const existingCash = existingData?.portfolio?.cash || 0
  
  // Update portfolio while preserving cash (strip undefined so Firestore accepts)
  const payload = stripUndefined({
    "portfolio.holdings": holdings,
    "portfolio.lastUpdated": Timestamp.now(),
    "portfolio.cash": existingCash,
  }) as Record<string, unknown>
  await updateDoc(userRef, payload)
}

// Update user cash balance
export async function updateUserCashInFirestore(
  userId: string,
  newCash: number
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  
  // Check if user document exists
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}. Please ensure user is created first.`)
  }
  
  await updateDoc(
    userRef,
    stripUndefined({
      "portfolio.cash": newCash,
      "portfolio.lastUpdated": Timestamp.now(),
    }) as Record<string, unknown>
  )
}

// Get all users (admin only)
export async function getAllUsersFromFirestore(): Promise<User[]> {
  try {
    console.log("Fetching all users from Firestore collection:", USERS_COLLECTION)
    const usersRef = collection(db, USERS_COLLECTION)
    const querySnapshot = await getDocs(usersRef)
    
    console.log(`Found ${querySnapshot.docs.length} documents in users collection`)
    
    const users = querySnapshot.docs
      .map((doc) => {
        try {
          const docData = doc.data()
          console.log(`Processing user document ${doc.id}:`, {
            hasEmail: !!docData.email,
            hasUsername: !!docData.username,
            hasPortfolio: !!docData.portfolio,
          })
          return firestoreToUser(docData, doc.id)
        } catch (error: any) {
          console.error(`Error converting user document ${doc.id}:`, {
            error: error?.message,
            code: error?.code,
          })
          // Return a minimal user object if conversion fails
          return {
            id: doc.id,
            email: doc.data()?.email || "",
            username: doc.data()?.username || "Unknown",
            avatar: doc.data()?.avatar,
            preferences: {
              theme: "system",
              riskTolerance: "medium",
              investmentFocus: "diversified",
              notifications: {
                email: false,
                targetPrice: false,
                ratingChange: false,
                majorNews: false,
              },
            },
            portfolio: {
              holdings: [],
              cash: 0,
              lastUpdated: new Date().toISOString(),
            },
            transactions: [],
            createdAt: new Date().toISOString(),
            isAdmin: false,
          }
        }
      })
      .filter((user) => user !== null) // Filter out any null values
    
    console.log(`Successfully converted ${users.length} users`)
    return users
  } catch (error: any) {
    console.error("Error in getAllUsersFromFirestore:", {
      error: error?.message,
      code: error?.code,
      stack: error?.stack,
    })
    throw error
  }
}

/** Normalize to valid date; use today if invalid or missing. */
function toValidDate(value: string | undefined): Date {
  if (!value || typeof value !== "string") return new Date()
  const d = new Date(value)
  return Number.isFinite(d.getTime()) ? d : new Date()
}

// Add transaction to user's transaction history
export async function addTransactionToFirestore(
  userId: string,
  transaction: {
    symbol: string
    type: "Buy" | "Sell"
    shares: number
    costPerShare: number
    transactionDate: string
    notes?: string
    assetType?: "stock" | "etf" | "crypto"
    name?: string
    providerId?: string
    fees?: number
    currency?: string
  }
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}`)
  }
  
  const existingData = userSnap.data()
  const existingTransactions = existingData?.transactions || []
  
  const execDate = toValidDate(transaction.transactionDate)
  const newTransaction: any = {
    id: `tx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    symbol: transaction.symbol,
    type: transaction.type,
    shares: transaction.shares,
    costPerShare: transaction.costPerShare,
    transactionDate: Timestamp.fromDate(execDate),
  }
  
  if (transaction.notes && transaction.notes.trim() !== "") {
    newTransaction.notes = transaction.notes
  }
  if (transaction.assetType) newTransaction.assetType = transaction.assetType
  if (transaction.name) newTransaction.name = transaction.name
  if (transaction.providerId) newTransaction.providerId = transaction.providerId
  if (Number.isFinite(transaction.fees) && transaction.fees! >= 0) newTransaction.fees = transaction.fees
  if (transaction.currency) newTransaction.currency = transaction.currency
  
  await updateDoc(
    userRef,
    stripUndefined({
      transactions: [...existingTransactions, newTransaction],
    }) as Record<string, unknown>
  )
}

/** Add multiple buy transactions in one update (e.g. from CSV import). */
export async function addTransactionsBatchToFirestore(
  userId: string,
  transactions: Array<{
    symbol: string
    shares: number
    costPerShare: number
    transactionDate: string
    fees?: number
    name?: string
    assetType?: "stock" | "etf" | "crypto"
    providerId?: string
    currency?: string
  }>
): Promise<void> {
  if (transactions.length === 0) return
  const userRef = doc(db, USERS_COLLECTION, userId)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}`)
  }
  const existingData = userSnap.data()
  const existingTransactions = existingData?.transactions || []
  const newTxs = transactions.map((tx) => {
    const execDate = toValidDate(tx.transactionDate)
    const t: any = {
      id: `tx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      symbol: tx.symbol,
      type: "Buy" as const,
      shares: tx.shares,
      costPerShare: tx.costPerShare,
      transactionDate: Timestamp.fromDate(execDate),
    }
    if (Number.isFinite(tx.fees) && tx.fees! >= 0) t.fees = tx.fees
    if (tx.name) t.name = tx.name
    if (tx.assetType) t.assetType = tx.assetType
    if (tx.providerId) t.providerId = tx.providerId
    if (tx.currency) t.currency = tx.currency
    return t
  })
  await updateDoc(
    userRef,
    stripUndefined({
      transactions: [...existingTransactions, ...newTxs],
    }) as Record<string, unknown>
  )
}

// Remove transaction from user's transaction history
export async function removeTransactionFromFirestore(
  userId: string,
  transactionId: string
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}`)
  }
  
  const existingData = userSnap.data()
  const existingTransactions = existingData?.transactions || []
  
  const updatedTransactions = existingTransactions.filter((tx: any) => tx.id !== transactionId)
  await updateDoc(
    userRef,
    stripUndefined({ transactions: updatedTransactions }) as Record<string, unknown>
  )
}

// Update user preferences
export async function updateUserPreferencesInFirestore(
  userId: string,
  preferences: { theme?: string; showValues?: boolean; [key: string]: any }
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, userId)
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    throw new Error(`User document does not exist for userId: ${userId}`)
  }
  
  // Get existing preferences and merge
  const existingData = userSnap.data()
  const existingPreferences = existingData?.preferences || {}
  
  const updatedPreferences = {
    ...existingPreferences,
    ...preferences,
  }
  await updateDoc(
    userRef,
    stripUndefined({ preferences: updatedPreferences }) as Record<string, unknown>
  )
}

